import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Volume2, Save, Radio, Play } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import Modal from '../ui/Modal';
import { eventActionsService as service, AudioSettings, ActionStatus, OutputDevice, outputLabels, voiceOptions, actionError } from '../../services/event-actions.service';

const defaults: AudioSettings = { enabled: false, output_type: 'builtin', device_id: null, channel_id: 1,
  volume: 60, voice: 'en', entry_direction: 'A_TO_B' };

const CameraEventAudio: React.FC<{ cameraId: number }> = ({ cameraId }) => {
  const [settings, setSettings] = useState<AudioSettings>(defaults);
  const [status, setStatus] = useState<ActionStatus | null>(null);
  const [devices, setDevices] = useState<OutputDevice[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoaded(false); setDirty(false); setError(''); setMessage(''); setJobId(null); setSettings(defaults);
    (async () => {
      const state = await service.status();
      if (!active) return;
      setStatus(state);
      if (!state.schema_ready) return;
      const data = await service.audio(cameraId);
      const available = await service.devices(data.location_id!);
      if (active) { setSettings(data); setDevices(available.items); setLoaded(true); }
    })().catch(err => { if (active) setError(actionError(err)); });
    return () => { active = false; };
  }, [cameraId]);

  useEffect(() => {
    if (!jobId || !settings.location_id) return;
    const controller = new AbortController();
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const until = Date.now() + 125000;
    const poll = async () => {
      try {
        const [history, available] = await Promise.all([
          service.jobs(settings.location_id!, controller.signal), service.devices(settings.location_id!, controller.signal),
        ]);
        if (stopped) return;
        setDevices(available.items);
        const job = history.items.find(item => item.id === jobId);
        if (job && !['pending', 'processing'].includes(job.status)) {
          setMessage(`${job.status}: ${job.result?.message || 'Command finished'}`); setJobId(null); return;
        }
        if (Date.now() >= until) { setMessage('Check Actions → Executions for the final result.'); setJobId(null); return; }
      } catch (err) { if (stopped) return; }
      if (!stopped) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 1500);
    return () => { stopped = true; controller.abort(); clearTimeout(timer); };
  }, [jobId, settings.location_id]);

  const update = (change: Partial<AudioSettings>) => { setSettings(prev => ({ ...prev, ...change })); setDirty(true); setMessage(''); };
  const save = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const data = await service.saveAudio(cameraId, settings);
      setSettings(data); setDirty(false);
      const available = await service.devices(data.location_id!); setDevices(available.items);
      setMessage('Event audio output saved. Configure greeting rules under Actions → Scenarios.');
    } catch (err) { setError(actionError(err)); } finally { setBusy(false); }
  };
  const command = async (kind: 'probe' | 'announce') => {
    if (!settings.device_id) return;
    setConfirm(false); setBusy(true); setError(''); setMessage('');
    try {
      const job = await service.command(settings.device_id, { kind, channel_id: settings.channel_id,
        volume: settings.volume, voice: settings.voice, message: 'Hello. This is an event audio test.' });
      setJobId(job.id); setMessage('Queued. Waiting for the action worker…');
    } catch (err) { setError(actionError(err)); } finally { setBusy(false); }
  };
  const target = devices.find(device => device.id === settings.device_id);
  const audioChannels = target?.capabilities?.audio_channels || [];
  const supported = audioChannels.some(channel => channel.id === settings.channel_id && channel.supported);
  const editable = loaded && !!status?.can_manage && !busy;

  return <Card padding="lg">
    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
      <div className="flex gap-3"><div className="p-2.5 rounded-xl bg-accent-primary/10 text-accent-primary h-fit"><Volume2 size={22} /></div>
        <div><h3 className="text-lg font-semibold text-text-primary">Event audio output</h3>
          <p className="text-sm text-text-muted">Choose where this camera’s event announcements play.</p></div></div>
      <Link className="text-sm text-accent-primary hover:underline" to="/actions">Manage speakers & scenarios</Link>
    </div>
    {error && <Alert variant="error" message={error} />}
    {message && <div role="status" className="text-sm text-text-secondary bg-surface-secondary rounded-lg p-3 mb-4">{message}</div>}
    {status && !status.schema_ready ? <Alert variant="info" message="Event audio setup is not ready on the API. Apply the event actions migration first." /> : <>
      {status && !status.worker_configured && <Alert variant="info" message="Settings can be saved. Start the separate action worker to discover outputs and play announcements." />}
      <fieldset disabled={!editable} className="space-y-5">
        <label className="flex items-center gap-3 text-sm font-medium text-text-primary">
          <input type="checkbox" checked={settings.enabled} onChange={e => update({ enabled: e.target.checked })} />Enable event audio
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <Select label="Audio output" value={settings.output_type}
            onChange={e => update({ output_type: e.target.value as AudioSettings['output_type'], device_id: null, channel_id: 1 })}
            options={(['builtin', 'ip_speaker', 'nvr'] as const).map(value => ({ value, label: outputLabels[value] }))} />
          {settings.output_type === 'ip_speaker' ? <Select label="IP speaker" value={settings.device_id || ''}
            onChange={e => update({ device_id: e.target.value || null, channel_id: 1 })}
            options={[{ value: '', label: 'Select a speaker' }, ...devices.filter(device => device.kind === 'ip_speaker' && device.enabled).map(device => ({ value: device.id, label: device.name }))]}
            helperText="Register speakers on Actions → Devices." /> :
            <div className="text-sm text-text-muted rounded-lg bg-surface-secondary p-3 self-end">
              {settings.output_type === 'builtin' ? 'Hik-Connect cameras use cloud SDK talkback. Other cameras use their saved NVR connection.' : 'Uses the location’s NVR HTTP/ISAPI address, including its saved port.'}
            </div>}
          {audioChannels.length ? <Select label="Audio channel" value={settings.channel_id} onChange={e => update({ channel_id: Number(e.target.value) })}>
            {!audioChannels.some(channel => channel.id === settings.channel_id) && <option value={settings.channel_id}>Channel {settings.channel_id} — not detected</option>}
            {audioChannels.map(channel => <option key={channel.id} value={channel.id} disabled={!channel.supported}>
              {channel.label || `Channel ${channel.id}`} · {channel.codec || 'Unknown codec'}{!channel.supported ? ' — unsupported' : ''}</option>)}
          </Select> : <Input label="Audio channel" type="number" min={1} max={9999} value={settings.channel_id}
            onChange={e => update({ channel_id: Number(e.target.value) })} helperText="Save, then check capabilities to discover the device’s audio channels." />}
          <Select label="Voice language" value={settings.voice} options={voiceOptions} onChange={e => update({ voice: e.target.value })} />
          <div><label htmlFor={`event-volume-${cameraId}`} className="field-label">Playback level · {settings.volume}%</label>
            <input id={`event-volume-${cameraId}`} className="w-full accent-accent-primary" type="range" min={1} max={100} value={settings.volume}
              onChange={e => update({ volume: Number(e.target.value) })} /><p className="text-xs text-text-muted">Relative to the device’s configured speaker volume.</p></div>
          <Select label="Entry direction" value={settings.entry_direction} onChange={e => update({ entry_direction: e.target.value as AudioSettings['entry_direction'] })}
            options={[{ value: 'A_TO_B', label: 'A → B is entry' }, { value: 'B_TO_A', label: 'B → A is entry' }]}
            helperText="Exit uses the reverse direction. Map this to the camera’s configured line." />
        </div>
      </fieldset>
      {target?.capabilities && <p className="text-sm text-text-muted mt-4">{target.capabilities.error ||
        (supported ? `${target.capabilities.route === 'hikconnect_sdk' ? 'Cloud SDK talkback available' : 'G.711 audio detected'} on channel ${settings.channel_id}. Use a test message to verify playback.` : 'This channel has no supported audio output. Check the device model, connection and selected channel.')}</p>}
      <div className="flex flex-wrap gap-3 mt-5">
        <Button type="button" leftIcon={<Save size={16} />} disabled={!editable} isLoading={busy} onClick={save}>Save audio settings</Button>
        <Button type="button" variant="outline" leftIcon={<Radio size={16} />} disabled={!editable || dirty || !settings.device_id || !status?.worker_configured || !!jobId} onClick={() => command('probe')}>Check capabilities</Button>
        <Button type="button" variant="outline" leftIcon={<Play size={16} />} disabled={!editable || dirty || !supported || !status?.worker_configured || !!jobId} onClick={() => setConfirm(true)}>Play test message</Button>
      </div>
      {dirty && <p className="text-xs text-text-muted mt-2">Save changes before checking or testing the selected output.</p>}
    </>}
    <Modal isOpen={confirm} onClose={() => setConfirm(false)} title="Play test announcement">
      <p className="text-text-secondary mb-4">Play “Hello. This is an event audio test.” on {target?.name}, channel {settings.channel_id}, at {settings.volume}% playback level?</p>
      <div className="flex justify-end gap-3"><Button type="button" variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
        <Button type="button" onClick={() => command('announce')}>Play on speaker</Button></div>
    </Modal>
  </Card>;
};

export default CameraEventAudio;
